import SiteLayout from "@/components/feature/SiteLayout";
import PageHero from "@/components/feature/PageHero";
import Reveal from "@/components/base/Reveal";
import Seo from "@/components/base/Seo";
import SectionHeading from "@/components/feature/SectionHeading";
import FeatureGrid from "@/components/feature/FeatureGrid";
import ProcessSteps from "@/components/feature/ProcessSteps";
import FaqAccordion from "@/components/feature/FaqAccordion";
import FinalCta from "@/pages/home/components/FinalCta";
import SupportChannels from "@/pages/suporte/components/SupportChannels";
import { supportStatus, supportTopics, supportFaqs } from "@/mocks/suporte";

const supportSteps = [
  {
    icon: "ri-chat-3-line",
    title: "Escolha o canal",
    description: "WhatsApp, e-mail ou formulário: fale com a gente do jeito que preferir.",
  },
  {
    icon: "ri-file-text-line",
    title: "Descreva a necessidade",
    description: "Conte o que está acontecendo com o seu projeto, com o máximo de detalhes.",
  },
  {
    icon: "ri-search-eye-line",
    title: "Analisamos o caso",
    description: "A nossa equipe avalia a situação e orienta os próximos passos.",
  },
  {
    icon: "ri-checkbox-circle-line",
    title: "Acompanhamos até resolver",
    description: "Seguimos com você até a sua solicitação estar atendida.",
  },
];

export default function Suporte() {
  return (
    <SiteLayout>
      <Seo
        title="Suporte e Atendimento Técnico | NXHOST"
        description="Suporte NXHOST para hospedagem, servidores, domínios, e-mail profissional e projetos digitais. Fale com a nossa equipe por WhatsApp, e-mail ou formulário."
        keywords="suporte hospedagem, atendimento técnico, suporte NXHOST, ajuda hospedagem, atendimento WhatsApp"
        path="/suporte"
        titleEn="Support and Technical Assistance | NXHOST"
        titleEs="Soporte y Atención Técnica | NXHOST"
        descriptionEn="NXHOST support for hosting, servers, domains, professional email and digital projects. Talk to our team via WhatsApp, email or form."
        descriptionEs="Soporte de NXHOST para hosting, servidores, dominios, correo profesional y proyectos digitales. Habla con nuestro equipo por WhatsApp, correo o formulario."
        breadcrumbs={[
          { name: "Início", path: "/" },
          { name: "Suporte", path: "/suporte" },
        ]}
        faqs={supportFaqs}
      />

      <PageHero
        badge="Suporte e atendimento"
        badgeIcon="ri-customer-service-2-line"
        title="Conte com a nossa equipe quando você precisar."
        subtitle="Atendimento próximo e humano para ajudar com hospedagem, servidores, domínios, e-mail profissional e os seus projetos digitais."
        crumbs={[{ label: "Início", to: "/" }, { label: "Suporte" }]}
      >
        <Reveal delay={220}>
          <div className="rounded-2xl border border-background-50/10 bg-foreground-900/60 p-5 backdrop-blur-sm">
            <div className="flex items-center justify-between">
              <span className="text-xs uppercase tracking-wide text-background-400">
                Status dos serviços
              </span>
              <span className="flex items-center gap-1.5 text-[11px] font-medium text-secondary-400">
                <span className="h-2 w-2 rounded-full bg-secondary-400"></span>
                Tudo certo
              </span>
            </div>
            <ul className="mt-4 space-y-3">
              {supportStatus.map((item) => (
                <li
                  key={item.label}
                  className="flex items-center justify-between gap-3 rounded-lg border border-background-50/10 bg-background-50/5 px-4 py-3"
                >
                  <span className="text-sm text-background-200">{item.label}</span>
                  <span className="flex items-center gap-2 text-xs font-medium text-secondary-400">
                    <i className="ri-checkbox-circle-line" aria-hidden="true"></i>
                    {item.value}
                  </span>
                </li>
              ))}
            </ul>
            <p className="mt-4 text-[11px] text-background-400">
              Status ilustrativo. Em caso de instabilidade, fale com a nossa equipe.
            </p>
          </div>
        </Reveal>
      </PageHero>

      <section className="bg-background-100 py-16 md:py-24">
        <div className="mx-auto max-w-7xl px-4 md:px-8">
          <SectionHeading
            badge="Canais de atendimento"
            badgeIcon="ri-contacts-book-2-line"
            title="Fale com a NXHOST pelo canal que preferir."
            description="Escolha o melhor jeito de falar com a nossa equipe e receba o atendimento de que precisa."
          />
          <div className="mt-12">
            <SupportChannels />
          </div>
        </div>
      </section>

      <section className="bg-background-50 py-16 md:py-24">
        <div className="mx-auto max-w-7xl px-4 md:px-8">
          <SectionHeading
            badge="Assuntos"
            badgeIcon="ri-list-check-2"
            badgeTone="secondary"
            title="Em que podemos ajudar você."
            description="Atendemos desde dúvidas rápidas até configurações e migrações mais complexas."
          />
          <div className="mt-12">
            <FeatureGrid items={supportTopics} columns={3} accent="secondary" />
          </div>
        </div>
      </section>

      <section className="bg-background-100 py-16 md:py-24">
        <div className="mx-auto max-w-7xl px-4 md:px-8">
          <SectionHeading
            badge="Como funciona"
            badgeIcon="ri-route-line"
            badgeTone="accent"
            title="Simples do começo ao fim."
            description="Um atendimento organizado para resolver o que você precisa sem complicação."
          />
          <div className="mt-12">
            <ProcessSteps steps={supportSteps} />
          </div>
        </div>
      </section>

      <section className="bg-background-50 py-16 md:py-24">
        <div className="mx-auto max-w-7xl px-4 md:px-8">
          <SectionHeading
            badge="Dúvidas frequentes"
            badgeIcon="ri-question-line"
            title="Perguntas sobre o suporte."
          />
          <div className="mt-12">
            <FaqAccordion items={supportFaqs} />
          </div>
        </div>
      </section>

      <FinalCta />
    </SiteLayout>
  );
}